import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiRequest } from '@/lib/queryClient';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  Upload,
  FileImage,
  Download,
  Trash2,
  Eye,
  Plus,
  Loader2,
  Image as ImageIcon,
  FileText,
  File,
} from 'lucide-react';
import { cn } from '@/lib/utils';

interface CustomerDocument {
  id: number;
  filename: string;
  originalFilename: string;
  mimeType?: string;
  fileSize: number;
  category: string;
  uploadedBy: number;
  createdAt: string;
  source?: string;
}

interface CustomerDocumentsProps {
  customerId: number;
}

export function CustomerDocuments({ customerId }: CustomerDocumentsProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [viewImage, setViewImage] = useState<string | null>(null);
  const [selectedDocument, setSelectedDocument] = useState<CustomerDocument | null>(null);
  const [showAllDocuments, setShowAllDocuments] = useState(true);
  const [deletingDocumentId, setDeletingDocumentId] = useState<number | null>(null);

  // Fetch customer documents
  const { data: documents = [], isLoading } = useQuery<CustomerDocument[]>({
    queryKey: [
      `/api/documents/customer-documents?customerId=${customerId}&includeAll=${showAllDocuments}`,
    ],
  });

  // Upload mutation with better optimization
  const uploadMutation = useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('customerId', customerId.toString());
      formData.append('category', 'customer-document');

      const response = await fetch('/api/documents/customer-documents/upload', {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to upload file');
      }

      return response.json();
    },
    onMutate: async (file: File) => {
      // Cancel any outgoing refetches for both query variations
      await queryClient.cancelQueries({
        queryKey: [`/api/documents/customer-documents?customerId=${customerId}&includeAll=true`],
      });
      await queryClient.cancelQueries({
        queryKey: [`/api/documents/customer-documents?customerId=${customerId}&includeAll=false`],
      });

      // Generate a temporary document for optimistic update
      const tempDoc: CustomerDocument = {
        id: -Date.now(), // Negative ID for temp docs
        filename: `uploading-${file.name}`,
        originalFilename: file.name,
        mimeType: file.type,
        fileSize: file.size,
        category: 'customer-document',
        uploadedBy: 0,
        createdAt: new Date().toISOString(),
        source: 'Customer Documents',
      };

      // Get current data for both query variations
      const previousDocumentsAll = queryClient.getQueryData<CustomerDocument[]>([
        `/api/documents/customer-documents?customerId=${customerId}&includeAll=true`,
      ]);
      const previousDocumentsOnly = queryClient.getQueryData<CustomerDocument[]>([
        `/api/documents/customer-documents?customerId=${customerId}&includeAll=false`,
      ]);

      // Optimistically update both query variations
      queryClient.setQueryData<CustomerDocument[]>(
        [`/api/documents/customer-documents?customerId=${customerId}&includeAll=true`],
        (old) => [...(old || []), tempDoc]
      );
      queryClient.setQueryData<CustomerDocument[]>(
        [`/api/documents/customer-documents?customerId=${customerId}&includeAll=false`],
        (old) => [...(old || []), tempDoc]
      );

      return { previousDocumentsAll, previousDocumentsOnly, tempDocId: tempDoc.id };
    },
    onSuccess: (data, _, context) => {
      // First, immediately remove the temp document by setting fresh data
      queryClient.setQueryData<CustomerDocument[]>(
        [`/api/documents/customer-documents?customerId=${customerId}&includeAll=true`],
        (old) => (old ? old.filter((doc) => doc.id >= 0) : []) // Remove temp docs (negative IDs)
      );
      queryClient.setQueryData<CustomerDocument[]>(
        [`/api/documents/customer-documents?customerId=${customerId}&includeAll=false`],
        (old) => (old ? old.filter((doc) => doc.id >= 0) : []) // Remove temp docs (negative IDs)
      );

      // Then force immediate refresh to get the real uploaded document
      queryClient.invalidateQueries({
        queryKey: [`/api/documents/customer-documents?customerId=${customerId}&includeAll=true`],
      });
      queryClient.invalidateQueries({
        queryKey: [`/api/documents/customer-documents?customerId=${customerId}&includeAll=false`],
      });

      toast({
        title: 'Success',
        description: 'Document uploaded successfully',
      });
    },
    onError: (error: Error, _, context) => {
      // Roll back both query variations on error
      if (context?.previousDocumentsAll) {
        queryClient.setQueryData(
          [`/api/documents/customer-documents?customerId=${customerId}&includeAll=true`],
          context.previousDocumentsAll
        );
      }
      if (context?.previousDocumentsOnly) {
        queryClient.setQueryData(
          [`/api/documents/customer-documents?customerId=${customerId}&includeAll=false`],
          context.previousDocumentsOnly
        );
      }

      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  // Delete mutation with improved optimization
  const deleteMutation = useMutation({
    mutationFn: async (documentId: number) => {
      setDeletingDocumentId(documentId);
      const response = await apiRequest(
        'DELETE',
        `/api/documents/customer-documents/${documentId}`
      );
      return response;
    },
    onMutate: async (documentId: number) => {
      // Cancel any outgoing refetches for both query variations
      await queryClient.cancelQueries({
        queryKey: [`/api/documents/customer-documents?customerId=${customerId}&includeAll=true`],
      });
      await queryClient.cancelQueries({
        queryKey: [`/api/documents/customer-documents?customerId=${customerId}&includeAll=false`],
      });

      // Snapshot the previous values for both queries
      const previousDocumentsAll = queryClient.getQueryData<CustomerDocument[]>([
        `/api/documents/customer-documents?customerId=${customerId}&includeAll=true`,
      ]);
      const previousDocumentsOnly = queryClient.getQueryData<CustomerDocument[]>([
        `/api/documents/customer-documents?customerId=${customerId}&includeAll=false`,
      ]);

      // Optimistically update both query variations (immediately remove from UI)
      queryClient.setQueryData<CustomerDocument[]>(
        [`/api/documents/customer-documents?customerId=${customerId}&includeAll=true`],
        (old) => (old ? old.filter((doc) => doc.id !== documentId) : [])
      );
      queryClient.setQueryData<CustomerDocument[]>(
        [`/api/documents/customer-documents?customerId=${customerId}&includeAll=false`],
        (old) => (old ? old.filter((doc) => doc.id !== documentId) : [])
      );

      return { previousDocumentsAll, previousDocumentsOnly };
    },
    onSuccess: () => {
      // Force immediate refresh of both query variations
      queryClient.invalidateQueries({
        queryKey: [`/api/documents/customer-documents?customerId=${customerId}`],
        refetchType: 'active',
      });

      toast({
        title: 'Success',
        description: 'Document deleted successfully',
      });
    },
    onError: (error, documentId, context) => {
      // Roll back both query variations on error
      if (context?.previousDocumentsAll) {
        queryClient.setQueryData(
          [`/api/documents/customer-documents?customerId=${customerId}&includeAll=true`],
          context.previousDocumentsAll
        );
      }
      if (context?.previousDocumentsOnly) {
        queryClient.setQueryData(
          [`/api/documents/customer-documents?customerId=${customerId}&includeAll=false`],
          context.previousDocumentsOnly
        );
      }

      toast({
        title: 'Error',
        description: 'Failed to delete document',
        variant: 'destructive',
      });
    },
    onSettled: () => {
      setDeletingDocumentId(null);
    },
  });

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Reset the input immediately to allow selecting the same file again
      event.target.value = '';

      // Auto-upload the file immediately (no manual loading state needed, optimistic update handles it)
      try {
        await uploadMutation.mutateAsync(file);
      } catch (error) {
        console.error('Upload failed:', error);
        // Error handling is done in the mutation's onError
      }
    }
  };

  const getFileIcon = (mimeType: string | undefined) => {
    if (!mimeType) return File;
    if (mimeType.startsWith('image/')) return ImageIcon;
    if (mimeType === 'application/pdf') return FileText;
    return File;
  };

  const handleView = (doc: CustomerDocument) => {
    if (doc.mimeType && doc.mimeType.startsWith('image/')) {
      setViewImage(`/api/documents/customer-documents/${doc.id}/view`);
      setSelectedDocument(doc);
    }
  };

  const handleDownload = async (doc: CustomerDocument) => {
    try {
      const response = await fetch(`/api/documents/customer-documents/${doc.id}/download`, {
        credentials: 'include',
      });

      if (!response.ok) throw new Error('Failed to download');

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.originalFilename || doc.filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to download document',
        variant: 'destructive',
      });
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <span>Customer Documents</span>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setShowAllDocuments(!showAllDocuments)}
                  className={cn(
                    'text-sm px-3 py-1 rounded-md transition-colors',
                    showAllDocuments
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted text-muted-foreground hover:bg-muted/80'
                  )}
                >
                  {showAllDocuments ? 'All Documents' : 'Uploaded Only'}
                </button>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="file"
                accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.csv"
                onChange={handleFileSelect}
                className="hidden"
                id="file-upload"
                disabled={uploadMutation.isPending}
              />
              <label htmlFor="file-upload">
                <Button variant="outline" size="sm" asChild disabled={uploadMutation.isPending}>
                  <span className="cursor-pointer">
                    {uploadMutation.isPending ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Uploading...
                      </>
                    ) : (
                      <>
                        <Upload className="h-4 w-4 mr-2" />
                        Upload File
                      </>
                    )}
                  </span>
                </Button>
              </label>
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {documents.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-12 h-12 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center mx-auto mb-4">
                <FileImage className="h-6 w-6 text-slate-500 dark:text-slate-400" />
              </div>
              <h3 className="text-lg font-medium text-slate-900 dark:text-slate-200 mb-2">
                No Documents Yet
              </h3>
              <p className="text-sm text-slate-500 dark:text-slate-400 max-w-md mx-auto">
                Upload site photos, PDFs, contracts, or other important documents related to this
                customer.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {documents.map((doc) => {
                const Icon = getFileIcon(doc.mimeType);
                const isImage = doc.mimeType && doc.mimeType.startsWith('image/');
                const isUploading = doc.id < 0; // Temp documents have negative IDs
                const isDeleting = deletingDocumentId === doc.id;

                return (
                  <div
                    key={doc.id}
                    className={cn(
                      'group relative border rounded-lg overflow-hidden transition-all',
                      'hover:shadow-md hover:border-primary/50',
                      (isUploading || isDeleting) && 'opacity-75'
                    )}
                  >
                    {/* Loading overlay for uploading/deleting */}
                    {(isUploading || isDeleting) && (
                      <div className="absolute inset-0 bg-white/80 flex items-center justify-center z-10 rounded-lg">
                        <div className="flex flex-col items-center gap-2">
                          <Loader2 className="h-8 w-8 animate-spin text-primary" />
                          <span className="text-sm font-medium">
                            {isUploading ? 'Uploading...' : 'Deleting...'}
                          </span>
                        </div>
                      </div>
                    )}

                    {isImage ? (
                      <div className="aspect-video bg-gray-100 relative overflow-hidden">
                        {!isUploading ? (
                          <img
                            src={`/api/documents/customer-documents/${doc.id}/view`}
                            alt={doc.originalFilename}
                            className="w-full h-full object-contain"
                            loading="lazy"
                            onError={(e) => {
                              e.currentTarget.src =
                                'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="100" height="100" viewBox="0 0 100 100"%3E%3Crect width="100" height="100" fill="%23f3f4f6"/%3E%3Ctext x="50" y="50" text-anchor="middle" dy=".3em" fill="%239ca3af" font-family="Arial" font-size="12"%3EImage Failed%3C/text%3E%3C/svg%3E';
                            }}
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center bg-gray-100">
                            <Icon className="h-12 w-12 text-gray-400" />
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="aspect-video bg-gray-50 flex items-center justify-center">
                        <Icon className="h-12 w-12 text-gray-400" />
                      </div>
                    )}

                    <div className="p-3">
                      <p className="font-medium text-sm truncate" title={doc.originalFilename}>
                        {doc.originalFilename}
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {doc.fileSize ? formatFileSize(doc.fileSize) : 'Unknown size'} •{' '}
                        {doc.createdAt
                          ? new Date(doc.createdAt).toLocaleDateString()
                          : 'Unknown date'}
                      </p>
                      {doc.source && showAllDocuments && (
                        <span
                          className={cn(
                            'inline-flex items-center px-2 py-0.5 rounded text-xs font-medium mt-2',
                            doc.source === 'Site Survey' &&
                              'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
                            doc.source === 'Installation' &&
                              'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
                            doc.source === 'Customer Documents' &&
                              'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200'
                          )}
                        >
                          {doc.source}
                        </span>
                      )}
                      <div className="flex items-center gap-1 mt-3">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDownload(doc)}
                          className="h-8 px-2"
                        >
                          <Download className="h-4 w-4" />
                        </Button>
                        {(!doc.source || doc.source === 'Customer Documents') && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              // Prevent multiple clicks
                              if (deletingDocumentId || deleteMutation.isPending) {
                                return;
                              }
                              deleteMutation.mutate(doc.id);
                            }}
                            disabled={deletingDocumentId === doc.id || deleteMutation.isPending}
                            className="h-8 px-2 text-destructive hover:text-destructive"
                          >
                            {deletingDocumentId === doc.id ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <Trash2 className="h-4 w-4" />
                            )}
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Image viewer dialog */}
      <Dialog open={!!viewImage} onOpenChange={() => setViewImage(null)}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>{selectedDocument?.originalFilename}</DialogTitle>
            <DialogDescription>
              Viewing document preview. Use keyboard arrows to navigate if multiple images are available.
            </DialogDescription>
          </DialogHeader>
          <div className="relative">
            {viewImage && (
              <img
                src={viewImage}
                alt={selectedDocument?.originalFilename}
                className="w-full h-auto"
              />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
